import { Injectable, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  forbiddenCharactersValidator,
  noWhitespaceValidator,
} from '../../../../shared/validators/custom-validators';
import {
  CourseGalleryItemDTO,
  TrainingPhaseType,
  TypeLearningPlanInstancePOST,
  TypePhase,
} from '../models/LearningPlanDTO';
import { LearningPlanService } from './learning-plan.service';
import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { GetCurrentUserData } from '../../../../core/store/current-user/current-user.state';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { LearningPlanInstanceDetailUiModel } from '../models/LearningPlanInstanceDetailUIModel';
import {
  firstValueFrom,
  startWith,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LearningPlanSetupComponent } from '../components/learning-plan-setup/learning-plan-setup.component';
import { LicenseCalculationsService } from '../../../payment/subscription/services/license-calculations.service';
import { CourseDetailsDTO } from '../../course/models/CourseDTO';
import { toCourseGalleryItemDTO } from '../../course/mappers/dto-mappers';
import { CourseGalleryService } from './course-gallery.service';
import { SuccessDialogComponent } from '../../../../shared/components/success-dialog/success-dialog.component';
import {
  DIALOG_SIZE_LARGE,
  DIALOG_SIZE_COMPACT,
} from '../../../../shared/components/dialog/dialog-sizes';
import { UserService } from '../../../organization/user/services/user.service';
import { UserResponseDTO } from '../../../organization/user/models/UserDTO';
import { alwaysSignal } from '../../../../core/signal-utils/alwaysSignal';

const BUSINESS_TIME_ZONE = 'Europe/Berlin';

export interface TypeLearningPlanSettings {
  learningPlanName: string | null;
  topicAreaId: string | null;
  duration: string | null;
  startDate: Date | null;
  notificationFrequency: string | null;
  deadlineType: 'fixed' | 'period' | null;
  endDate: Date | null;
}

export interface FormGroupSettings {
  learningPlanName: FormControl<TypeLearningPlanSettings['learningPlanName']>;
  topicAreaId: FormControl<TypeLearningPlanSettings['topicAreaId']>;
  duration: FormControl<TypeLearningPlanSettings['duration']>;
  startDate: FormControl<TypeLearningPlanSettings['startDate']>;
  notificationFrequency: FormControl<
    TypeLearningPlanSettings['notificationFrequency']
  >;
  deadlineType: FormControl<TypeLearningPlanSettings['deadlineType']>;
  endDate: FormControl<TypeLearningPlanSettings['endDate']>;
}
export interface FormGroupCourseSelection {
  loop: FormControl<boolean>;
  phases: FormArray<FormGroup<FormGroupPhase>>;
}

export interface FormGroupPhase {
  course: FormControl<CourseGalleryItemDTO | null>;
}

@Injectable({
  providedIn: 'root',
})
export class LearningPlanBuilderDataService {
  private readonly formBuilder = inject(FormBuilder);
  private readonly learningPlanService = inject(LearningPlanService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly store = inject(Store);

  private readonly arrowRedrawTriggerSignal = alwaysSignal(0);
  readonly arrowRedrawTrigger = this.arrowRedrawTriggerSignal.asReadonly();
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly licenseCalculationsService = inject(
    LicenseCalculationsService,
  );
  private readonly courseGalleryService = inject(CourseGalleryService);
  private readonly userService = inject(UserService);

  private topicAreaSubscription: Subscription | null = null;
  private durationSubscription: Subscription | null = null;
  private deadlineTypeSubscription: Subscription | null = null;
  private cachedUsersPromise: Promise<UserResponseDTO[]> | null = null;

  public readonly durationOptions = [
    {
      value: 'month',
      key: 'month',
    },
    {
      value: 'quarter',
      key: 'quarter',
    },
    {
      value: 'year',
      key: 'year',
    },
  ];

  private readonly durationToTrainingPhaseType: Record<
    string,
    TrainingPhaseType
  > = {
    month: 'Monthly',
    quarter: 'Quarterly',
    year: 'Yearly',
  };

  private readonly trainingPhaseTypeToDuration: Partial<
    Record<TrainingPhaseType, string>
  > = {
    ['Monthly']: 'month',
    ['Quarterly']: 'quarter',
    ['Yearly']: 'year',
  };

  public isLoading = false;
  public editedLearningPlanId: string | null;
  public topicAreas: Array<{ key: string; value: string }>;
  private defaultTopicAreaId: string | null = null;
  public formGroupSettings: FormGroup<FormGroupSettings> =
    this.createInitialFormGroupSettings();
  private readonly selectedStudentsSubject$: Subject<Array<{ id: string }>> =
    new Subject();

  public formGroupCourseSelection: FormGroup<FormGroupCourseSelection> =
    this.createInitialFormGroupCourseSelection();

  private readonly today: Date = new Date();
  public minDate: Date | null = null;
  public maxDate: Date | null = new Date(
    this.today.getFullYear() + 5,
    this.today.getMonth(),
    this.today.getDate(),
  );
  public allowedMonths: number[] | null;

  public selectedStudents: Array<{ id: string }> = [];

  public licensesToAssignCount = 0;

  private originallySelectedCourse: CourseDetailsDTO | null = null;

  public get minEndDate(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const startDate = this.formGroupSettings.controls.startDate.value;
    if (!startDate) {
      return tomorrow;
    }

    const dayAfterStartDate = new Date(startDate);
    dayAfterStartDate.setDate(dayAfterStartDate.getDate() + 1);
    dayAfterStartDate.setHours(0, 0, 0, 0);

    return dayAfterStartDate > tomorrow ? dayAfterStartDate : tomorrow;
  }

  constructor() {
    this.fetchAvailableTopicAreas();
    this.setupTopicAreaChangeListener();
    this.observeDurationChanges();
    this.observeDeadlineTypeChanges();
  }

  private observeDeadlineTypeChanges(): void {
    if (this.deadlineTypeSubscription) {
      this.deadlineTypeSubscription.unsubscribe();
    }

    const deadlineTypeControl = this.formGroupSettings.controls.deadlineType;
    const endDateControl = this.formGroupSettings.controls.endDate;
    this.deadlineTypeSubscription = deadlineTypeControl.valueChanges.subscribe(
      (deadlineType) => {
        this.formGroupSettings.controls.startDate.setValue(
          deadlineType === 'fixed' ? new Date() : this.getCurrentStartOfMonth(),
        );
        endDateControl.setValidators(
          deadlineType === 'fixed' ? Validators.required : null,
        );
        endDateControl.updateValueAndValidity();
      },
    );
  }

  private getCurrentStartOfMonth(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  triggerArrowRedraw() {
    this.arrowRedrawTriggerSignal.set(0);
  }

  private observeDurationChanges(): void {
    if (this.durationSubscription) {
      this.durationSubscription.unsubscribe();
    }

    const durationControl = this.formGroupSettings.controls.duration;
    this.durationSubscription = durationControl.valueChanges
      .pipe(startWith(durationControl.value))
      .subscribe((duration: string | null) => {
        this.setDateRestrictions(duration);
      });
  }

  private readonly setDateRestrictions = (duration: string | null): void => {
    if (!duration) {
      return;
    }
    switch (duration) {
      case 'year':
        this.minDate = new Date(
          this.today.getFullYear() - 1,
          this.today.getMonth() + 1,
          1,
        );
        this.allowedMonths = null;
        break;
      case 'quarter':
        this.minDate = new Date(
          this.today.getFullYear(),
          this.today.getMonth() - 2,
          1,
        );
        this.allowedMonths = null;
        break;
      case 'month':
        this.minDate = new Date(
          this.today.getFullYear(),
          this.today.getMonth(),
          1,
        );
        this.allowedMonths = null;
        break;
      default:
        this.minDate = null;
        this.allowedMonths = null;
    }

    this.formGroupSettings.controls.startDate.updateValueAndValidity();
  };

  private fetchAvailableTopicAreas() {
    this.learningPlanService.getTopicAreas().subscribe({
      next: (response) => {
        this.topicAreas = response.topicAreas.map((topicArea) => ({
          key: topicArea.name,
          value: topicArea.id,
        }));
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }

  private setupTopicAreaChangeListener(): void {
    if (this.topicAreaSubscription) {
      this.topicAreaSubscription.unsubscribe();
    }

    this.topicAreaSubscription =
      this.formGroupSettings.controls.topicAreaId.valueChanges.subscribe(() => {
        this.resetPhases();
      });
  }

  public resetPhasesAndLoop(): void {
    this.resetPhases();
    this.resetLoop();
  }

  public resetPhases(): void {
    this.formGroupCourseSelection.controls.phases.clear();
    this.formGroupCourseSelection.controls.phases.push(
      this.createPhaseFormGroup(),
    );
    this.formGroupCourseSelection.updateValueAndValidity();
  }

  public resetLoop(): void {
    this.formGroupCourseSelection.controls.loop.setValue(false);
    this.formGroupCourseSelection.updateValueAndValidity();
  }

  public resetTopicArea(): void {
    this.formGroupSettings.controls.topicAreaId.setValue(
      this.defaultTopicAreaId,
    );
  }

  public setDefaultTopicAreaId(topicAreaId: string | null): void {
    this.defaultTopicAreaId = topicAreaId;
    this.formGroupSettings = this.createInitialFormGroupSettings();
    this.setupTopicAreaChangeListener();
    this.observeDurationChanges();
    this.observeDeadlineTypeChanges();
  }

  public reset(): void {
    this.editedLearningPlanId = null;
    this.formGroupSettings = this.createInitialFormGroupSettings();
    this.formGroupCourseSelection =
      this.createInitialFormGroupCourseSelection();
    this.setSelectedStudents([]);
    this.licensesToAssignCount = 0;
    this.invalidateUserCache();
    this.setupTopicAreaChangeListener();
    this.observeDurationChanges();
    this.observeDeadlineTypeChanges();
  }

  private createInitialFormGroupSettings(): FormGroup<FormGroupSettings> {
    const currentStartOfMonth = this.getCurrentStartOfMonth();
    return this.formBuilder.group<FormGroupSettings>({
      learningPlanName: this.formBuilder.control(
        { value: '', disabled: this.isLoading },
        {
          validators: [
            Validators.required,
            Validators.maxLength(50),
            forbiddenCharactersValidator(),
            noWhitespaceValidator(),
          ],
        },
      ),
      topicAreaId: this.formBuilder.control(
        { value: this.defaultTopicAreaId, disabled: this.isLoading },
        { updateOn: 'blur', validators: Validators.required },
      ),
      duration: this.formBuilder.control(
        { value: 'year', disabled: this.isLoading },
        { updateOn: 'blur', validators: Validators.required },
      ),
      startDate: this.formBuilder.control(
        { value: currentStartOfMonth, disabled: this.isLoading },
        { updateOn: 'blur', validators: Validators.required },
      ),
      notificationFrequency: this.formBuilder.control(
        { value: 'monthly', disabled: true },
        { updateOn: 'blur', validators: Validators.required },
      ),
      deadlineType: this.formBuilder.control(
        { value: 'period', disabled: this.isLoading },
        { updateOn: 'change', validators: Validators.required },
      ),
      endDate: this.formBuilder.control(
        { value: null, disabled: this.isLoading },
        { updateOn: 'blur' },
      ),
    });
  }

  private createInitialFormGroupCourseSelection(): FormGroup<FormGroupCourseSelection> {
    return this.formBuilder.group<FormGroupCourseSelection>({
      phases: this.formBuilder.array([this.createPhaseFormGroup()]),
      loop: this.formBuilder.nonNullable.control({
        value: false,
        disabled: this.isLoading,
      }),
    });
  }

  public createPhaseFormGroup(
    value: CourseGalleryItemDTO | null = null,
  ): FormGroup<FormGroupPhase> {
    return this.formBuilder.group({
      course: this.formBuilder.control(
        { value, disabled: this.isLoading },
        { updateOn: 'blur', validators: Validators.required },
      ),
    });
  }

  public getPhasesArray(): FormArray<FormGroup<FormGroupPhase>> {
    return this.formGroupCourseSelection.controls.phases;
  }

  public addPhase(): void {
    this.getPhasesArray().push(this.createPhaseFormGroup());
    this.formGroupCourseSelection.updateValueAndValidity();
  }

  public setCourseId(index: number, value: CourseGalleryItemDTO | null): void {
    this.getPhasesArray().controls[index].controls.course.setValue(value);
    this.formGroupCourseSelection.updateValueAndValidity();
  }

  public removePhase(index: number): void {
    const phasesArray = this.getPhasesArray();
    if (phasesArray.length > 1) {
      phasesArray.removeAt(index, { emitEvent: true });
    }
    this.formGroupCourseSelection.updateValueAndValidity();
  }

  public setEditedLearningPlan(
    learningPlanInstance: LearningPlanInstanceDetailUiModel | null,
  ) {
    if (learningPlanInstance) {
      const isCustom = learningPlanInstance.trainingPhaseType === 'Custom';

      this.editedLearningPlanId = learningPlanInstance.id;
      this.formGroupSettings.controls.learningPlanName.setValue(
        learningPlanInstance.name,
      );
      this.formGroupSettings.controls.duration.setValue(
        isCustom
          ? null
          : (this.trainingPhaseTypeToDuration[
              learningPlanInstance.trainingPhaseType
            ] ?? null),
      );
      this.formGroupSettings.controls.topicAreaId.setValue(
        learningPlanInstance.topicArea?.id ?? null,
      );
      this.formGroupSettings.controls.deadlineType.setValue(
        isCustom ? 'fixed' : 'period',
      );
      this.formGroupSettings.controls.startDate.setValue(
        new Date(learningPlanInstance.startAt),
      );
      const customEndsAt = learningPlanInstance.orderedPhases[0]?.endsAt;
      this.formGroupSettings.controls.endDate.setValue(
        isCustom && customEndsAt ? new Date(customEndsAt) : null,
      );
      this.formGroupCourseSelection.controls.phases = this.formBuilder.array(
        learningPlanInstance.orderedPhases.map(({ courseInstances }) =>
          this.createPhaseFormGroup({
            id: courseInstances[0].courseId,
            aboutUrl: courseInstances[0].aboutUrl ?? '',
            name: courseInstances[0].name,
            externalMoodleId: '',
            description: '',
          }),
        ),
      );
      const lastPhase = learningPlanInstance.orderedPhases.at(-1);
      const firstPhase = learningPlanInstance.orderedPhases.at(0);
      this.formGroupCourseSelection.controls.loop.setValue(
        !!firstPhase && !!lastPhase && lastPhase.nextPhaseId === firstPhase.id,
      );
    } else {
      this.reset();
    }
  }

  public createTrainingWithEnrollment(): Subscription | null {
    if (this.isLoading) {
      return null;
    }
    const trainingData = this._getLearningPlanData();
    if (!trainingData) {
      return null;
    }
    this.isLoading = true;
    const willSendInvitations = this.willSendInvitations(trainingData);
    const students = this.getSelectedStudents();

    if (students.length > 0) {
      return this.learningPlanService
        .createLearningPlanInstance(trainingData)
        .pipe(
          switchMap((response) => {
            const trainingId = response.learningPlanInstance.id;
            return this.learningPlanService.enrollStudentsToLearningPlan(
              students,
              trainingId,
            );
          }),
        )
        .subscribe({
          next: () => {
            this.handleSuccessfulCreation(
              trainingData.learningPlanInstance.name,
              willSendInvitations,
            );
          },
          error: () => {
            this.isLoading = false;
          },
        });
    } else {
      return this.learningPlanService
        .createLearningPlanInstance(trainingData)
        .subscribe({
          next: () => {
            this.handleSuccessfulCreation(
              trainingData.learningPlanInstance.name,
              willSendInvitations,
            );
          },
          error: () => {
            this.isLoading = false;
          },
        });
    }
  }

  willSendInvitations(trainingData: TypeLearningPlanInstancePOST): boolean {
    // TODO: check for Learning Plan SCHEDULED status instead of comparing dates. Need to return status from backend first (only id is returned).
    return new Date(trainingData.learningPlanInstance.startDate) < new Date();
  }

  handleSuccessfulCreation(
    trainingName: string,
    willSendInvitations = false,
  ): void {
    this.isLoading = false;
    this.reset();
    void this.router.navigate([`/portal/dashboard/`]);

    this.dialog.open(SuccessDialogComponent, {
      ...DIALOG_SIZE_COMPACT,
      data: {
        title: 'portal.trainings.setup.successDialog.title',
        description: willSendInvitations
          ? 'portal.trainings.setup.successDialog.descriptionWithInvitations'
          : 'portal.trainings.setup.successDialog.descriptionWithoutInvitations',
        descriptionParams: { trainingName: trainingName },
        buttonText: 'portal.trainings.setup.successDialog.buttonText',
      },
    });
    this.store.dispatch(new GetCurrentUserData());
    this.licenseCalculationsService.refreshLicenses();
  }

  private _getLearningPlanData(): TypeLearningPlanInstancePOST | undefined {
    const {
      learningPlanName,
      topicAreaId,
      startDate,
      duration,
      deadlineType,
      endDate,
    } = this.formGroupSettings.getRawValue();
    const { phases, loop } = this.formGroupCourseSelection.getRawValue();

    if (
      !learningPlanName ||
      !topicAreaId ||
      !startDate ||
      (deadlineType !== 'fixed' && !duration) ||
      (deadlineType === 'fixed' && !endDate)
    ) {
      return undefined;
    }

    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    const trainingPhaseType: TrainingPhaseType =
      deadlineType === 'fixed'
        ? 'Custom'
        : this.durationToTrainingPhaseType[duration ?? ''];

    const endsAt =
      deadlineType === 'fixed' && endDate
        ? formatter.format(endDate).replace(' ', 'T') +
          this.getTimeZoneOffset(endDate)
        : undefined;

    const payload: TypeLearningPlanInstancePOST = {
      learningPlanInstance: {
        name: learningPlanName,
        topicAreaId: topicAreaId,
        startDate: this.formatStartOfDay(startDate),
        looping: loop,
        trainingPhaseType,
        phases: phases.reduce<TypePhase[]>((acc, { course }, index) => {
          if (course) {
            acc.push({
              name: `${index + 1}`,
              endsAt,
              courses: [course.id],
            });
          }
          return acc;
        }, []),
      },
    };

    return payload;
  }

  public createLearningPlanInstance() {
    if (this.isLoading) {
      return;
    }

    const learningPlanInstance = this._getLearningPlanData();

    if (!learningPlanInstance) {
      return;
    }

    this.isLoading = true;

    this.learningPlanService
      .createLearningPlanInstance(learningPlanInstance)
      .subscribe({
        next: (response) => {
          this.store.dispatch(new GetCurrentUserData());
          this.isLoading = false;
          const learningPlanId = response.learningPlanInstance.id;
          const path = learningPlanId
            ? `/portal/learningPlans/${learningPlanId}`
            : `/portal/dashboard/`;
          this.reset();
          void this.router.navigate([path]);
        },
        error: (error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          this.isLoading = false;
        },
      });
  }

  public editLearningPlanInstance() {
    if (this.isLoading) {
      return;
    }

    const learningPlanInstance = this._getLearningPlanData();

    if (!learningPlanInstance || !this.editedLearningPlanId) {
      return;
    }

    this.isLoading = true;

    this.learningPlanService
      .editLearningPlanInstance(this.editedLearningPlanId, learningPlanInstance)
      .subscribe({
        next: () => {
          this.store.dispatch(new GetCurrentUserData());
          this.isLoading = false;
          const learningPlanId = this.editedLearningPlanId;
          const path = learningPlanId
            ? `/portal/learningPlans/${learningPlanId}`
            : `/portal/dashboard/`;
          this.reset();
          void this.router.navigate([path]);
        },
        error: (error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          this.isLoading = false;
        },
      });
  }

  openDialog(course: CourseDetailsDTO | null): void {
    if (course) {
      toCourseGalleryItemDTO(course, this.courseGalleryService).subscribe({
        next: (galleryItem) => {
          this.setCourseId(0, galleryItem);
        },
      });
    }
    this.dialog.open(LearningPlanSetupComponent, {
      ...DIALOG_SIZE_LARGE,
      panelClass: 'learning-plan-setup-dialog-panel',
      data: {
        isCustomTraining: false,
      },
    });
  }

  public async prefetchUsers(): Promise<UserResponseDTO[]> {
    this.cachedUsersPromise ??= firstValueFrom(
      this.userService.getActiveUsers(),
    ).then((response) => response.users);
    return await this.cachedUsersPromise;
  }

  public invalidateUserCache(): void {
    this.cachedUsersPromise = null;
  }

  public setSelectedStudents(students: Array<{ id: string }>): void {
    this.selectedStudents = students;
    this.selectedStudentsSubject$.next(students);
  }

  public getSelectedStudents(): Array<{ id: string }> {
    return this.selectedStudents;
  }

  public setLicensesToAssignCount(count: number): void {
    this.licensesToAssignCount = count;
  }

  public setOriginallySelectedCourse(course: CourseDetailsDTO): void {
    this.originallySelectedCourse = course;
  }

  public getOriginallySelectedCourse(): CourseDetailsDTO | null {
    return this.originallySelectedCourse;
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .format(date)
      .replace(' ', 'T');
  }

  private formatStartOfDay(date: Date): string {
    const datePart = new Intl.DateTimeFormat('sv-SE', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
    return `${datePart}T00:00:00${this.getTimeZoneOffset(date)}`;
  }

  private getTimeZoneOffset(date: Date): string {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(
      date.toLocaleString('en-US', { timeZone: BUSINESS_TIME_ZONE }),
    );
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60_000;

    const sign = offsetMinutes >= 0 ? '+' : '-';
    const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(
      2,
      '0',
    );
    const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
    return `${sign}${hours}:${minutes}`;
  }
}
